/**
 * FmCorridorTrajectoryChart.labels — the SVG label and marker renderers of
 * the corridor-trajectory figure: trajectory dots, collision-managed point
 * labels, and the subject label (nearest clear offset above its dot).
 *
 * Every renderer records its estimated text box in the shared per-render
 * registry (see FmCorridorTrajectoryChart.layout) so later labels can avoid
 * or yield to earlier ones — and every candidate slot is ALSO tested against
 * the registered series strokes: a label with no stroke-free slot renders on
 * an opaque band-colour plate rather than transparently over a line
 * (visual-QA F2). The rate identities and the statutory floor are decoded by
 * the legend alone — the plot carries no rate identity tags and no in-plot
 * floor text (visual-QA F1).
 */
import React from 'react';
import type { LabelProps as RechartsLabelProps } from 'recharts';
import type {
  CorridorLabelRegistry,
  LabelBoxRect,
  PointLabelPlan,
} from './FmCorridorTrajectoryChart.layout';
import {
  LABEL_ABOVE_DY,
  LABEL_BELOW_DY,
  LABEL_H,
  SUBJECT_CHAR_W,
  SUBJECT_OFFSETS,
  textBox,
} from './FmCorridorTrajectoryChart.layout';
import type { FmStoryCorridorTrajectoryPoint } from './FmCorridorTrajectory.types';

export type TrajectoryDatum = FmStoryCorridorTrajectoryPoint & {
  pointIndex: number;
};

interface TrajectoryDotProps {
  cx?: number;
  cy?: number;
  payload?: TrajectoryDatum;
  stroke?: string;
}

export function trajectoryDot(
  pathId: string,
  color: string,
  lastIndex: number,
): (props: TrajectoryDotProps) => React.ReactElement {
  const TrajectoryDot = ({ cx, cy, payload }: TrajectoryDotProps): React.ReactElement => {
    if (cx === undefined || cy === undefined || payload === undefined) {
      return <g key={`${pathId}-dot-missing`} />;
    }
    const key = `${pathId}-dot-${payload.pointIndex}`;
    if (payload.pointIndex === lastIndex && lastIndex > 0) {
      // Terminal marker: a filled arrowhead-style triangle states the time
      // direction of the path without inventing intermediate data.
      const arrow = `${cx},${cy - 5.5} ${cx + 5},${cy + 4} ${cx - 5},${cy + 4}`;
      return <polygon key={key} points={arrow} fill={color} stroke="none" />;
    }
    if (payload.operationLabel) {
      // Decision-year marker: a ringed dot where the branch persisted an
      // operation on this step.
      return (
        <circle
          key={key}
          cx={cx}
          cy={cy}
          r={4.5}
          fill="var(--fm-chart-halo)"
          stroke={color}
          strokeWidth={2}
        />
      );
    }
    return <circle key={key} cx={cx} cy={cy} r={2.5} fill={color} stroke="none" />;
  };
  return TrajectoryDot;
}

export interface PointLabelRenderProps {
  x?: number | string;
  y?: number | string;
  index?: number;
  value?: unknown;
}

/** Recharts hands label positions as number|string: parse or refuse. */
function numericPixel(
  x: number | string | undefined,
  y: number | string | undefined,
): { x: number; y: number } | null {
  if (x === undefined || y === undefined) return null;
  const numericX = Number(x);
  const numericY = Number(y);
  return Number.isFinite(numericX) && Number.isFinite(numericY)
    ? { x: numericX, y: numericY }
    : null;
}

/**
 * An invisible per-point tracer on a drawn series: it renders nothing, but
 * teaches the registry the data→pixel transform from the REAL coordinates
 * Recharts hands it, so the registered data-space series obstacles resolve to
 * pixel geometry for the stroke-collision test (visual-QA F2).
 */
export function seriesObserver(
  seriesId: string,
  data: readonly { x: number; y: number }[],
  registry: CorridorLabelRegistry,
): (props: PointLabelRenderProps) => React.ReactElement {
  const SeriesObserver = ({ x, y, index }: PointLabelRenderProps): React.ReactElement => {
    const key = `${seriesId}-observe-${index ?? 'missing'}`;
    const pixel = numericPixel(x, y);
    const point = index === undefined ? undefined : data[index];
    if (pixel !== null && point !== undefined) {
      registry.observe(point.x, point.y, pixel.x, pixel.y);
    }
    return <g key={key} />;
  };
  return SeriesObserver;
}

/** Register the real ReferenceLine geometry for collision-aware labels. */
export function floorObstacle(
  registry: CorridorLabelRegistry,
  floorValue: number,
  xDomain: readonly [number, number],
): (props: RechartsLabelProps) => React.ReactElement {
  const FloorObstacle = ({ viewBox }: RechartsLabelProps): React.ReactElement => {
    const cartesianViewBox = viewBox && 'x' in viewBox ? viewBox : undefined;
    if (
      cartesianViewBox?.x === undefined ||
      cartesianViewBox.y === undefined ||
      cartesianViewBox.width === undefined
    ) {
      return <g />;
    }
    if (cartesianViewBox.width > 0) {
      registry.observe(xDomain[0], floorValue, cartesianViewBox.x, cartesianViewBox.y);
      registry.observe(
        xDomain[1],
        floorValue,
        cartesianViewBox.x + cartesianViewBox.width,
        cartesianViewBox.y,
      );
    }
    return <g />;
  };
  return FloorObstacle;
}

interface LabelPlacement {
  box: LabelBoxRect;
  dx: number;
  dy: number;
  anchor: 'middle' | 'start' | 'end';
  /** Far slots draw a hairline back to their point. */
  leader: boolean;
  plate: boolean;
}

/** Horizontal gap between a point and a label set beside it. */
const SIDE_DX = 9;
/** Baseline offsets of the far slots that carry a leader line. */
const FAR_ABOVE_DY = -30;
const FAR_BELOW_DY = 40;

/**
 * Candidate slots nearest first: the planned side, the opposite side, beside
 * the point to the right and left, then one step farther above and below on
 * a leader line. Several operations in one year (thin, then plant) pile up
 * on one x; the extra slots give each its own clear spot.
 */
function pointLabelCandidates(
  planned: PointLabelPlan,
  pixel: { x: number; y: number },
): Omit<LabelPlacement, 'plate'>[] {
  const slot = (
    dx: number,
    dy: number,
    anchor: LabelPlacement['anchor'],
    leader = false,
  ): Omit<LabelPlacement, 'plate'> => ({
    box: textBox(pixel.x + dx, pixel.y + dy, planned.text, anchor),
    dx,
    dy,
    anchor,
    leader,
  });
  const above = slot(0, LABEL_ABOVE_DY, 'middle');
  const below = slot(0, LABEL_BELOW_DY, 'middle');
  return [
    ...(planned.side === 'above' ? [above, below] : [below, above]),
    slot(SIDE_DX, LABEL_H / 3, 'start'),
    slot(-SIDE_DX, LABEL_H / 3, 'end'),
    slot(0, FAR_ABOVE_DY, 'middle', true),
    slot(0, FAR_BELOW_DY, 'middle', true),
  ];
}

/**
 * Choose the label's slot: the first candidate clear of BOTH other labels and
 * series strokes. When none is stroke-free, the first label-clear slot takes
 * an opaque plate — never transparent text across a line. A plain year label
 * with no label-clear near slot renders nothing; an operation label always
 * renders.
 */
function choosePointLabelPlacement(
  planned: PointLabelPlan,
  pixel: { x: number; y: number },
  key: string,
  registry: CorridorLabelRegistry,
): LabelPlacement | null {
  const candidates = pointLabelCandidates(planned, pixel);
  const labelClear = candidates.filter((candidate) => !registry.collides(candidate.box, key));
  if (!planned.plate) {
    const fullyClear = labelClear.find((candidate) => !registry.collidesWithSeries(candidate.box));
    if (fullyClear) return { ...fullyClear, plate: false };
  }
  const fallback = labelClear[0];
  if (fallback && (planned.hasOperation || planned.plate || !fallback.leader)) {
    return { ...fallback, plate: true };
  }
  // An operation is the decision the chart exists to show: with every slot
  // taken it still prints, plated, in its planned slot. Plain years drop.
  return planned.hasOperation || planned.plate ? { ...candidates[0], plate: true } : null;
}

interface LabelElementSpec {
  key: string;
  box: LabelBoxRect;
  x: number;
  baselineY: number;
  text: string;
  className: string;
  plate: boolean;
  anchor?: 'middle' | 'start' | 'end';
  /** Point a far label belongs to; draws the leader hairline. */
  leaderTo?: { x: number; y: number };
}

/** Shared SVG output: the label text, optionally on an opaque plate and a leader. */
function labelElement(spec: LabelElementSpec): React.ReactElement {
  const { key, box, x, baselineY, text, className, plate, anchor = 'middle', leaderTo } = spec;
  const leaderY = leaderTo && leaderTo.y > box.y2 ? box.y2 : box.y1;
  return (
    <g key={key}>
      {leaderTo ? (
        <line
          className="fm-corridor-trajectory__leader"
          x1={leaderTo.x}
          y1={leaderTo.y}
          x2={leaderTo.x}
          y2={leaderY}
        />
      ) : null}
      {plate ? (
        <rect
          className="fm-corridor-trajectory__label-plate"
          x={box.x1 - 2}
          y={box.y1 - 1}
          width={box.x2 - box.x1 + 4}
          height={box.y2 - box.y1 + 2}
          rx={2}
        />
      ) : null}
      <text x={x} y={baselineY} textAnchor={anchor} className={className}>
        {text}
      </text>
    </g>
  );
}

export function trajectoryPointLabel(
  pathId: string,
  data: readonly TrajectoryDatum[],
  plan: ReadonlyMap<number, PointLabelPlan>,
  subject: { x: number; y: number },
  registry: CorridorLabelRegistry,
): (props: PointLabelRenderProps) => React.ReactElement {
  const TrajectoryPointLabel = ({ x, y, index }: PointLabelRenderProps): React.ReactElement => {
    const key = `${pathId}-label-${index ?? 'missing'}`;
    const pixel = numericPixel(x, y);
    const point = index === undefined ? undefined : data[index];
    if (index === undefined || pixel === null || point === undefined) return <g key={key} />;
    // Every trajectory point teaches the registry the data→pixel transform.
    registry.observe(point.x, point.y, pixel.x, pixel.y);
    if (point.x === subject.x && point.y === subject.y) {
      // The subject label anchors to this pixel; record it so every later
      // label can collision-test against the subject's zone.
      registry.setSubjectAnchor(pixel.x, pixel.y);
    }
    const planned = plan.get(index);
    const placement = planned ? choosePointLabelPlacement(planned, pixel, key, registry) : null;
    if (!planned || !placement) {
      registry.drop(key);
      return <g key={key} />;
    }
    registry.place(key, placement.box);
    return labelElement({
      key,
      box: placement.box,
      x: pixel.x + placement.dx,
      baselineY: pixel.y + placement.dy,
      text: planned.text,
      className: 'fm-corridor-trajectory__point-label',
      plate: placement.plate,
      anchor: placement.anchor,
      leaderTo: placement.leader ? pixel : undefined,
    });
  };
  return TrajectoryPointLabel;
}

export function subjectLabelContent(
  subject: { label: string; x: number; y: number },
  registry: CorridorLabelRegistry,
  alwaysPlate = false,
): (props: RechartsLabelProps) => React.ReactElement {
  const label = subject.label;
  const SubjectLabel = ({ x, y, viewBox }: RechartsLabelProps): React.ReactElement => {
    const linePixel = numericPixel(x, y);
    const cartesianViewBox = viewBox && 'x' in viewBox ? viewBox : undefined;
    const cx = linePixel?.x ?? cartesianViewBox?.x;
    const cy = linePixel?.y ?? cartesianViewBox?.y;
    if (cx === undefined || cy === undefined) return <g />;
    // The subject's own data→pixel pair completes the y transform for charts
    // whose only other observation is the floor line (the 2019 band).
    registry.observe(subject.x, subject.y, cx, cy);
    registry.setSubjectAnchor(cx, cy);
    // Collision-test the offset ladder twice: first for a slot clear of both
    // labels and strokes (plain text), then for a label-clear slot that still
    // crosses a stroke (opaque plate). The ladder prefers above the dot but
    // extends BELOW it too — open space under the subject beats a plate over
    // a band edge. The subject always renders.
    const candidates = [
      ...SUBJECT_OFFSETS.map((offset) => cy - 6 - offset),
      ...SUBJECT_OFFSETS.map((offset) => cy + 6 + offset + LABEL_H),
    ].map((baseline) => ({
      baseline,
      box: textBox(cx, baseline, label, 'middle', SUBJECT_CHAR_W),
    }));
    const labelClear = candidates.filter(
      (candidate) => !registry.collides(candidate.box, 'subject'),
    );
    let chosen: (typeof candidates)[number] | undefined;
    let plate: boolean;
    if (alwaysPlate) {
      // Even a plated compact label prefers a stroke-free slot: an opaque
      // plate over the hero curve whites out published evidence (visual-QA
      // round-2 n5), so open air wins whenever the ladder offers it.
      chosen = labelClear.find((candidate) => !registry.collidesWithSeries(candidate.box));
      chosen ??= labelClear[0];
      plate = true;
    } else {
      chosen = labelClear.find((candidate) => !registry.collidesWithSeries(candidate.box));
      plate = chosen === undefined;
      if (chosen === undefined) chosen = labelClear[0];
    }
    if (chosen === undefined) {
      chosen = candidates[candidates.length - 1];
      plate = true;
    }
    registry.place('subject', chosen.box);
    return labelElement({
      key: 'subject',
      box: chosen.box,
      x: cx,
      baselineY: chosen.baseline,
      text: label,
      className: 'fm-corridor-trajectory__subject-label',
      plate,
    });
  };
  return SubjectLabel;
}
